import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { HasAccess } from "@/components/common/has-access";
import {
  useScheduledBroadcasts,
  useUpdateScheduledBroadcast,
  useCancelScheduledBroadcast,
} from "../hooks/use-broadcast";
import { ScheduledBroadcastRecord } from "../services/broadcast";
import { summariseAudience } from "../utils/summarise-audience";

const STATUS_STYLES: Record<ScheduledBroadcastRecord["status"], string> = {
  SCHEDULED: "bg-blue-100 text-blue-700",
  PAUSED: "bg-amber-100 text-amber-700",
  COMPLETED: "bg-green-100 text-green-700",
  CANCELLED: "bg-gray-100 text-gray-600",
  FAILED: "bg-red-100 text-red-700",
};

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function scheduleLabel(b: ScheduledBroadcastRecord): string {
  if (b.kind === "ONCE") {
    return b.scheduled_at
      ? `Once · ${format(new Date(b.scheduled_at), "PPp")}`
      : "Once";
  }
  const r = b.recurrence;
  if (!r) return "Recurring";
  if (r.frequency === "WEEKLY") {
    const days = (r.weekdays ?? []).map((d) => DOW[d]).join(", ");
    return `Weekly · ${days} · ${r.time} IST`;
  }
  if (r.frequency === "MONTHLY") {
    return `Monthly · day ${r.day_of_month} · ${r.time} IST`;
  }
  return `Daily · ${r.time} IST`;
}


const TH = "pb-2 pr-4 font-medium";

export function ScheduledBroadcasts() {
  const { data, isLoading } = useScheduledBroadcasts();
  const { mutate: updateStatus, isPending: updating } =
    useUpdateScheduledBroadcast();
  const { mutate: cancel, isPending: cancelling } =
    useCancelScheduledBroadcast();
  const busy = updating || cancelling;

  if (isLoading)
    return <p className="text-sm text-gray-400">Loading…</p>;
  if (!data || data.length === 0)
    return (
      <p className="text-sm text-gray-400">No scheduled broadcasts yet.</p>
    );

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-xs uppercase tracking-wide text-gray-400">
              <th className={TH}>Title</th>
              <th className={TH}>Audience</th>
              <th className={TH}>Schedule</th>
              <th className={TH}>Next run</th>
              <th className={TH}>Runs</th>
              <th className={TH}>Status</th>
              <th className="pb-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.map((b) => {
              const active = b.status === "SCHEDULED";
              const paused = b.status === "PAUSED";
              return (
                <tr key={b.id} className="border-b border-gray-50">
                  <td className="py-2 pr-4 font-medium text-gray-800">
                    {b.title}
                  </td>
                  <td className="max-w-[180px] truncate py-2 pr-4 text-gray-500">
                    {summariseAudience(b.filters)}
                  </td>
                  <td className="py-2 pr-4 text-gray-600">
                    {scheduleLabel(b)}
                  </td>
                  <td className="py-2 pr-4 text-gray-600">
                    {b.next_run_at
                      ? format(new Date(b.next_run_at), "PPp")
                      : "—"}
                  </td>
                  <td className="py-2 pr-4 text-gray-700">{b.run_count}</td>
                  <td className="py-2 pr-4">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[b.status]}`}
                    >
                      {b.status}
                    </span>
                  </td>
                  <td className="py-2">
                    {active || paused ? (
                      <HasAccess permission="write">
                        <div className="flex gap-2">
                          {active && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busy}
                              onClick={() =>
                                updateStatus({ id: b.id, status: "PAUSED" })
                              }
                            >
                              Pause
                            </Button>
                          )}
                          {paused && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busy}
                              onClick={() =>
                                updateStatus({ id: b.id, status: "SCHEDULED" })
                              }
                            >
                              Resume
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700"
                            disabled={busy}
                            onClick={() => cancel(b.id)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </HasAccess>
                    ) : (
                      <span className="text-xs text-gray-300">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
